import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import ContactRail from "@/components/ContactRail";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ImmobileDettaglio() {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [fotoAttiva, setFotoAttiva] = useState(0);

  useEffect(() => {
    axios
      .get(`${API}/properties/${id}`)
      .then((res) => {
        setProperty(res.data);
        setFotoAttiva(0);
      })
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <section className="bg-white py-32 px-6" data-testid="detail-not-found">
        <div className="max-w-6xl mx-auto text-center space-y-4">
          <h1 className="font-display font-bold text-3xl">Immobile non trovato</h1>
          <p className="text-sm text-[var(--ink)]/70">L'annuncio potrebbe essere stato venduto o ritirato.</p>
          <Link to="/vendita" className="inline-block font-mono-lbl text-xs uppercase text-[var(--gold-deep)] font-semibold hover:underline">
            ← Torna agli immobili
          </Link>
        </div>
      </section>
    );
  }

  if (!property) {
    return (
      <section className="bg-white py-32 px-6">
        <p className="max-w-6xl mx-auto font-mono-lbl text-xs uppercase text-[var(--ink)]/50" data-testid="detail-loading">
          Caricamento immobile...
        </p>
      </section>
    );
  }

  const specs = [
    property.mq ? { label: "Metratura", value: `${new Intl.NumberFormat("it-IT").format(property.mq)} mq` } : null,
    property.locali ? { label: "Locali", value: property.locali } : null,
    property.bagni ? { label: "Bagni", value: property.bagni } : null,
    property.piano ? { label: "Piano", value: property.piano } : null,
    property.stato ? { label: "Stato", value: property.stato } : null,
    property.riscaldamento ? { label: "Riscaldamento", value: property.riscaldamento } : null,
    property.spese_label ? { label: "Spese condominiali", value: property.spese_label } : null,
    property.zona ? { label: "Zona", value: property.zona } : null,
    { label: "Tipologia", value: property.categoria },
  ].filter(Boolean);

  const backTo = property.tipo === "affitto" ? "/affitti" : "/vendita";
  const backLabel = property.tipo === "affitto" ? "Tutti gli affitti" : "Tutte le vendite";

  const galleria = property.galleria && property.galleria.length > 0 ? property.galleria : [property.image_url];

  return (
    <>
      <section className="bg-white py-24 md:py-32 px-6" data-testid="detail-page">
        <div className="max-w-6xl mx-auto">
          <Link
            to={backTo}
            data-testid="detail-back-link"
            className="inline-block font-mono-lbl text-xs uppercase tracking-wider text-[var(--gold-deep)] font-semibold hover:underline mb-8"
          >
            ← {backLabel}
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <figure className="relative aspect-[4/3] bg-[#eceae4] border border-[var(--ink)]/10 rounded-lg overflow-hidden">
                <img
                  src={galleria[fotoAttiva]}
                  alt={property.image_alt}
                  className="absolute inset-0 w-full h-full object-cover"
                  data-testid="detail-main-photo"
                />
                <figcaption className="absolute bottom-5 left-5 font-mono-lbl text-xs uppercase text-[var(--ink)] bg-white/90 px-3 py-1 rounded">
                  {property.badge}
                </figcaption>
                {galleria.length > 1 && (
                  <span className="absolute bottom-5 right-5 font-mono-lbl text-[10px] bg-[var(--ink)]/80 text-white px-2 py-1 rounded">
                    {fotoAttiva + 1} / {galleria.length}
                  </span>
                )}
              </figure>
              {galleria.length > 1 && (
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mt-3" data-testid="detail-gallery">
                  {galleria.map((url, i) => (
                    <button
                      key={url}
                      type="button"
                      onClick={() => setFotoAttiva(i)}
                      data-testid={`detail-thumb-${i}`}
                      className={`relative aspect-[4/3] rounded overflow-hidden border-2 transition-colors ${
                        i === fotoAttiva ? "border-[var(--gold)]" : "border-transparent hover:border-[var(--gold)]/50"
                      }`}
                    >
                      <img src={url} alt={`${property.image_alt} — foto ${i + 1}`} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="lg:col-span-5 space-y-6">
              <div>
                <span className="font-mono-lbl text-xs uppercase tracking-widest text-[var(--gold-deep)] font-semibold">
                  {property.tipo === "affitto" ? "In Affitto" : "In Vendita"} · {property.categoria}
                </span>
                <h1 className="font-display font-bold text-[clamp(28px,3.4vw,44px)] leading-tight mt-2">
                  {property.titolo}
                </h1>
                <p className="text-base text-[var(--ink)]/75 leading-relaxed mt-3">{property.descrizione}</p>
              </div>

              <div className="border-y border-[var(--ink)]/10 py-4 font-mono-lbl">
                <span className="text-[10px] uppercase tracking-widest text-[var(--ink)]/50 block mb-1">
                  {property.tipo === "affitto" ? "Canone" : "Prezzo"}
                </span>
                <span className="font-bold text-3xl text-[var(--ink)]" data-testid="detail-price">
                  {property.prezzo_label}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3" data-testid="detail-specs">
                {specs.map((s) => (
                  <div key={s.label} className="bg-[var(--paper)] border border-[var(--ink)]/10 rounded p-3">
                    <span className="font-mono-lbl text-[10px] uppercase tracking-wider text-[var(--ink)]/50 block mb-1">
                      {s.label}
                    </span>
                    <span className="text-sm font-semibold text-[var(--ink)]">{s.value}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Link
                  to="/#contatto"
                  data-testid="detail-cta-contatto"
                  className="flex-1 text-center px-6 py-3.5 bg-[var(--ink)] text-[var(--gold-light)] font-mono-lbl text-xs uppercase tracking-widest font-bold rounded hover:bg-[var(--gold)] hover:text-[var(--ink)] transition-colors"
                >
                  Richiedi Visita →
                </Link>
                <a
                  href="https://wa.me/393349604516"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="detail-cta-whatsapp"
                  className="flex-1 text-center px-6 py-3.5 bg-white border border-[var(--ink)]/20 text-[var(--ink)] font-mono-lbl text-xs uppercase tracking-widest font-bold rounded hover:border-[var(--gold)] transition-colors"
                >
                  WhatsApp
                </a>
              </div>
              <p className="font-mono-lbl text-[10px] uppercase tracking-wider text-[var(--ink)]/40">
                Annuncio pubblicato da Home Consulting di Vianne Ascari · Modena
              </p>
              {property.link_annuncio && (
                <a
                  href={property.link_annuncio}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="detail-external-link"
                  className="inline-block font-mono-lbl text-[10px] uppercase tracking-wider text-[var(--gold-deep)] hover:underline"
                >
                  Vedi anche su immobiliare.it ↗
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      <ContactRail />
    </>
  );
}
